import DataView from '@/components/data-view/data-view';
import DeleteConfirmationModal from '@/components/modal/DeleteConfirmationModal';
import { TableConfig } from '@/components/table/types';
import { useCohorts, useDeleteCohort } from '@/hooks/queries/cohorts';
import { Cohort } from '@/types/cohort';
import { Icon } from '@iconify/react';
import { Button, Card, Spinner, Tooltip } from '@nextui-org/react';
import React from 'react';
import CohortModal from './components/cohort-modal';

const CohortsPage = () => {
  const { data: cohorts, isLoading, refetch } = useCohorts();
  const [viewType, setViewType] = React.useState<'tile' | 'list'>('list');
  const [isFormModalOpen, setIsFormModalOpen] = React.useState(false);
  const [selectedCohort, setSelectedCohort] = React.useState<Cohort | null>(
    null
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [selectedCohortToDelete, setSelectedCohortToDelete] = React.useState<
    number | null
  >(null);

  const deleteMutation = useDeleteCohort();

  const handleAdd = () => {
    setSelectedCohort(null);
    setIsFormModalOpen(true);
  };

  const handleEdit = (cohort: Cohort) => {
    setSelectedCohort(cohort);
    setIsFormModalOpen(true);
  };

  const handleDelete = (id: number) => {
    setSelectedCohortToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (!selectedCohortToDelete) return;

    deleteMutation.mutate(selectedCohortToDelete, {
      onSuccess: () => {
        refetch();
        setIsDeleteModalOpen(false);
        setSelectedCohortToDelete(null);
      },
      onError: () => {
        setIsDeleteModalOpen(false);
        setSelectedCohortToDelete(null);
      },
    });
  };

  const tableConfig: TableConfig = {
    columns: [
      { uid: 'id', name: 'ID', sortable: true },
      { uid: 'name', name: 'Name', sortable: true },
      {
        uid: 'is_default',
        name: 'Default',
        sortable: true,
        renderCell: (item: Cohort) => (item.is_default ? 'Yes' : 'No'),
      },
      {
        uid: 'actions',
        name: 'Actions',
        renderCell: (item: Cohort) => (
          <div className="flex items-center gap-2">
            <Tooltip content="Edit">
              <Button
                isIconOnly
                variant="light"
                size="sm"
                onPress={() => handleEdit(item)}
              >
                <Icon icon="lucide:edit" className="w-5 h-5" />
              </Button>
            </Tooltip>
            <Tooltip content="Delete">
              <Button
                isIconOnly
                variant="light"
                size="sm"
                color="danger"
                onPress={() => handleDelete(item.id)}
              >
                <Icon icon="lucide:trash-2" className="w-5 h-5" />
              </Button>
            </Tooltip>
          </div>
        ),
      },
    ],
    initialVisibleColumns: ['id', 'name', 'is_default', 'actions'],
    searchable: false,
    disableDefaultSort: true,
  };

  const actions = (
    <Button
      color="primary"
      startContent={<Icon icon="lucide:plus" />}
      onPress={handleAdd}
    >
      Add Cohort
    </Button>
  );

  const closeFormModal = () => {
    setIsFormModalOpen(false);
    setSelectedCohort(null);
  };

  return (
    <div className="flex flex-col h-full">
      {isLoading ? (
        <div className="flex h-full min-h-0 flex-1 items-center justify-center">
          <Spinner size="lg" />
        </div>
      ) : !cohorts?.results?.length ? (
        <div className="flex-1 flex items-center justify-center">
          <Card className="p-12 flex flex-col items-center justify-center text-center">
            <Icon icon="lucide:users" className="w-12 h-12 text-default-300" />
            <h3 className="text-xl font-semibold mt-4">No Cohorts Yet</h3>
            <p className="text-default-500 mt-2 max-w-md">
              Create your first cohort to start organizing users into groups.
            </p>
            <Button
              color="primary"
              className="mt-6"
              startContent={<Icon icon="lucide:plus" />}
              onPress={handleAdd}
            >
              Add Cohort
            </Button>
          </Card>
        </div>
      ) : (
        <DataView
          title="Cohorts"
          actions={actions}
          data={cohorts.results}
          tableConfig={tableConfig}
          viewType={viewType}
          onViewChange={setViewType}
          renderCard={() => null}
          disableTileView
        />
      )}

      <CohortModal
        isOpen={isFormModalOpen}
        onClose={closeFormModal}
        cohort={selectedCohort}
      />
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Cohort"
        entityName="cohort"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};

export default CohortsPage;
